import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Arrow } from 'icons/untitledui';
import { DropdownMenu } from 'shared/components/dropdown/DropdownMenu';
import * as Text from 'theme/components/Text.style';

import {
  buildComposeUrl,
  EMAIL_PROVIDERS,
  EmailComposePayload,
  EmailProvider,
  PROVIDER_LABEL_KEYS,
} from './providers';

export interface EmailComposeMenuTriggerProps {
  isOpen: boolean;
  open: (e: React.SyntheticEvent<HTMLElement>) => void;
  close: () => void;
  toggle: (e: React.SyntheticEvent<HTMLElement>) => void;
}

interface EmailComposeMenuProps {
  payload: EmailComposePayload;
  onSelected?: (provider: EmailProvider) => void;
  renderTrigger: (props: EmailComposeMenuTriggerProps) => React.ReactNode;
  usePortal?: boolean;
}

export default function EmailComposeMenu({
  payload,
  onSelected,
  renderTrigger,
  usePortal = true,
}: EmailComposeMenuProps): JSX.Element {
  const { t } = useTranslation('shared');
  const [isOpen, setIsOpen] = useState(false);

  const items = useMemo(
    () =>
      EMAIL_PROVIDERS.map((provider) => ({
        label: <Text.P2>{t(PROVIDER_LABEL_KEYS[provider])}</Text.P2>,
        icon: <Arrow.ArrowUpRight size={20} />,
        onSelect: ({ close }: { close: () => void }) => {
          window.open(
            buildComposeUrl(provider, payload),
            '_blank',
            'noopener,noreferrer'
          );
          onSelected?.(provider);
          close();
        },
      })),
    [t, payload, onSelected]
  );

  return (
    <DropdownMenu
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      items={items}
      headerLabel={t('emailCompose.shareWith')}
      renderTrigger={({ isOpen: triggerIsOpen, open, close, toggle }) =>
        renderTrigger({ isOpen: triggerIsOpen, open, close, toggle })
      }
      usePortal={usePortal}
    />
  );
}
